import React, {useEffect, useMemo, useState} from "react";
import {Box} from "@mui/system";
import {Button, Typography} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import {ButtonGroupTabBar} from "../../../components/UI/button/ButtonGroupTabBar";
import TextFieldValidation from "../../../components/UI/table/customer-detales/TextFieldValidation";
import ButtonAdd from "../../../components/UI/button/ButtonAdd";
import VariablesTable from "../../../components/UI/table/customer-detales/processing/VariablesTable";
import {withFilter} from "../../../data/FilterTables/withFilterHOC";
import {VariablesFilterData} from "../../../data/FilterTables/VariablesFilterData";
import {EditLocalStoragePagesData} from "../../../utils/EditLocalStoragePagesData";
import PacedModulesService from "../../../API/organization/PacedModulesService";
import {
    asVariablesList,
    buildPacedMainFormPayload,
    emptyPointForm,
    pacedDtoToMainForm,
} from "./module3d/pacedDetailModule";

const PointFields = ({title, point, onPointChange}) => (
    <Box sx={{display: "flex", flexDirection: "column", gap: 0}}>
        <Typography sx={{fontSize: "14px", fontWeight: 600, mt: 1, mb: 0.5}}>{title}</Typography>
        {["x", "y", "z"].map((axis) => (
            <TextFieldValidation
                key={axis}
                name={axis}
                type={"text"}
                value={point?.[axis] ?? "0"}
                label={axis.toUpperCase()}
                setCurrValues={(updater) => {
                    onPointChange((prevPoint) => {
                        const next = typeof updater === "function" ? updater(prevPoint) : updater;
                        return next;
                    });
                }}
                required={false}
            />
        ))}
    </Box>
);

const applyDtoToState = (data, setters) => {
    const {setDto, setForm, setVariables, setTab, savedTab, tableOptionsHandle} = setters;
    setDto(data);
    setForm(pacedDtoToMainForm(data));
    const vars = asVariablesList(data?.variables);
    setVariables(vars);
    setTab(savedTab === 2 ? 2 : 1);
    tableOptionsHandle((prev) => ({
        ...prev,
        totalElements: vars.length,
    }));
};

const PacedModulePlacementPanel = ({
    pacedModule,
    onPacedModuleChange,
    handleModalAlert,
    tabStoragePath,
}) => {
    const ComponentVariablesWithFilter = useMemo(() => {
        return withFilter(VariablesTable, VariablesFilterData);
    }, []);
    const innerTabStoragePath = `${tabStoragePath}/paced-module`;

    const [loading, setLoading] = useState(false);
    const [dto, setDto] = useState(pacedModule);
    const [form, setForm] = useState(() => pacedDtoToMainForm(pacedModule));
    const [variables, setVariables] = useState(() => asVariablesList(pacedModule?.variables));
    const [tab, setTab] = useState(() => {
        const pagesData = JSON.parse(localStorage.getItem("pagesData")) || {};
        const savedTab = pagesData[innerTabStoragePath]?.tab ?? 1;
        return Number(savedTab) === 2 ? 2 : 1;
    });
    const [tableOptions, setTableOptions] = useState({
        pageIndex: 0,
        pageSize: 100,
        totalElements: asVariablesList(pacedModule?.variables).length,
        searchData: "",
    });

    const tableOptionsHandle = (nextState) => {
        setTableOptions(nextState);
        if (nextState?.pageSize != null && nextState.pageSize !== tableOptions.pageSize) {
            EditLocalStoragePagesData(innerTabStoragePath, "pageSize", nextState.pageSize);
        }
    };

    useEffect(() => {
        if (pacedModule == null) {
            return;
        }
        const pagesData = JSON.parse(localStorage.getItem("pagesData")) || {};
        const savedTab = pagesData[innerTabStoragePath]?.tab ?? 1;
        applyDtoToState(pacedModule, {
            setDto,
            setForm,
            setVariables,
            setTab,
            savedTab,
            tableOptionsHandle,
        });
    }, [pacedModule, innerTabStoragePath]);

    const handleChangeTab = (nextTab) => {
        setTab(nextTab);
        tableOptionsHandle((prev) => ({
            ...prev,
            totalElements: nextTab === 2 ? variables.length : prev.totalElements,
        }));
        EditLocalStoragePagesData(innerTabStoragePath, "tab", nextTab);
    };

    const setPoint = (key) => (updater) => {
        setForm((prev) => {
            const current = prev[key] || emptyPointForm();
            const next = typeof updater === "function" ? updater(current) : updater;
            return {...prev, [key]: next};
        });
    };

    const setVariablesFromTable = (next) => {
        const list = typeof next === "function" ? next(variables) : next;
        setVariables(list);
        const nextDto = dto ? {...dto, variables: list} : {variables: list};
        setDto(nextDto);
        onPacedModuleChange?.(nextDto);
    };

    const pacedRecordId = dto?.id != null && Number(dto.id) !== 0 ? dto.id : pacedModule?.id;

    const handleUpdate = () => {
        if (pacedRecordId == null) {
            handleModalAlert({message: "Нет id размещения"});
            return;
        }
        setLoading(true);
        const payload = buildPacedMainFormPayload(form, dto, pacedRecordId);
        PacedModulesService.update(pacedRecordId, payload)
            .then((response) => {
                const data = response.data;
                setDto(data);
                setForm(pacedDtoToMainForm(data));
                setVariables(asVariablesList(data?.variables));
                onPacedModuleChange?.(data);
                handleModalAlert("Данные изменены", "success");
            })
            .catch((error) => {
                handleModalAlert(error);
            })
            .finally(() => setLoading(false));
    };

    const generalTabComponents = [
        {
            tabIndex: 1,
            label: "Основные",
            component: (
                <Box sx={{width: "400px", display: "flex", flexDirection: "column", gap: 0}}>
                    <PointFields
                        title="Точка привязки"
                        point={form.anchor}
                        onPointChange={setPoint("anchor")}
                    />
                    <PointFields
                        title="axesX"
                        point={form.axesX}
                        onPointChange={setPoint("axesX")}
                    />
                    <PointFields
                        title="axesY"
                        point={form.axesY}
                        onPointChange={setPoint("axesY")}
                    />
                    <PointFields
                        title="axesZ"
                        point={form.axesZ}
                        onPointChange={setPoint("axesZ")}
                    />
                    <Box sx={{
                        width: 400,
                        paddingTop: "20px",
                        display: "flex",
                        justifyContent: "flex-end",
                    }}>
                        <ButtonAdd loading={loading} text={"Применить"} onClick={handleUpdate}/>
                    </Box>
                </Box>
            ),
        },
        {
            tabIndex: 2,
            label: "Переменные",
            component: (
                <ComponentVariablesWithFilter
                    data={variables}
                    setData={setVariablesFromTable}
                    enableEditing={true}
                    tableOptions={tableOptions}
                    tableOptionsHandle={tableOptionsHandle}
                    pacedModule
                    pacedModuleId={pacedRecordId}
                />
            ),
        },
    ];

    return (
        <Box sx={{display: "flex", flexDirection: "column", gap: "32px"}}>
            <ButtonGroupTabBar activeTab={tab}>
                {generalTabComponents.map(({tabIndex, label}) => (
                    <Button
                        key={tabIndex}
                        tabIndex={tabIndex}
                        onClick={() => handleChangeTab(tabIndex)}
                    >
                        <CheckIcon/>
                        {label}
                    </Button>
                ))}
            </ButtonGroupTabBar>
            {generalTabComponents.find((c) => c.tabIndex === tab)?.component}
        </Box>
    );
};

export default PacedModulePlacementPanel;
